import { createI18n } from 'vue-i18n'
import sr from './locales/sr.json'
import en from './locales/en.json'

/**
 * The catalogue is Influx's own, not Studio's.
 *
 * Almost nothing overlaps — Influx says "pick a site", "enter your code",
 * Studio says everything about pages and media — so sharing one file would
 * ship Studio's entire vocabulary in this bundle to reuse a handful of words.
 * What IS shared is the user's CHOICE of language, which lives on their
 * account and reaches both apps through `/api/user/preferences`.
 */

/**
 * Serbian has three plural forms, selected by the last digit(s):
 *   1, 21, 31…  → singular   ("1 stranica")
 *   2-4, 22-24… → paucal     ("3 stranice")
 *   everything else, incl. 5-20 and 11-14 → plural ("857 stranica")
 * vue-i18n's default rule only handles two forms, which gets Serbian wrong
 * for every count above one.
 */
function serbianPlural(choice: number, choicesLength: number): number {
  if (choicesLength < 3) return choice === 1 ? 0 : 1

  const mod10 = choice % 10
  const mod100 = choice % 100

  if (mod10 === 1 && mod100 !== 11) return 0
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1
  return 2
}

export type Locale = 'sr' | 'en'

/**
 * The BCP 47 tag to hand `Intl`, which is not the catalogue's key.
 *
 * ICU resolves bare `sr` to Serbian CYRILLIC, so every date, number and
 * relative time formatted with the catalogue key came out in the wrong script
 * on a Latin interface — "пре 2 сата" under "Nedavno otvarano". The product is
 * `sr-Latn` and has to say so.
 *
 * Anything that reaches `Intl` goes through here; the catalogue keeps its
 * short keys, which is what the locale files are named by.
 */
export function intlLocale(locale: string): string {
  return locale === 'sr' ? 'sr-Latn' : locale
}

/**
 * The language to use before the account is known.
 *
 * Local storage is a CACHE here, not the source of truth: it answers the first
 * paint, and `applyPreferences` overwrites it the moment `/api/auth/me`
 * returns. For a share recipient, who never has an account, it stays the
 * answer — which is why the code screen carries its own language picker.
 */
const CACHE_KEY = 'influx-locale'

function cachedLocale(): Locale {
  try {
    return localStorage.getItem(CACHE_KEY) === 'en' ? 'en' : 'sr'
  } catch {
    // Private windows and blocked site data both throw on access.
    return 'sr'
  }
}

export const i18n = createI18n({
  legacy: false,
  locale: cachedLocale(),
  fallbackLocale: 'en',
  messages: { sr, en },
  pluralRules: { sr: serbianPlural },
})

/** Switches language and remembers it for the next first paint. */
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale
  try {
    localStorage.setItem(CACHE_KEY, locale)
  } catch {
    // Nothing to do — the account still carries the preference.
  }
}

document.documentElement.lang = i18n.global.locale.value
